import { AppError, PollingError, type AppErrorSource } from "config/errors";
import { IFormInput, IFormInputs, ErrorCodes, GqlValidationError, IAppError } from "config/types";

import { GraphQLError } from "graphql";
import { camelCase, isEqual, isString, kebabCase, mapValues, some } from "lodash-es";

// ---------------------------------------------------------------------------
// GraphQL / auth error builders
// ---------------------------------------------------------------------------

/**
 * Universal error adapter for thunk catch blocks.
 *
 * Detects whether the error originated from GraphQL or the auth REST API
 * and returns a plain IAppError. Delegates to AppError for classification.
 */
export const getAppError = (response: AppErrorSource): IAppError => {
  return new AppError(response).toPlainObject();
};

// ---------------------------------------------------------------------------
// Form validation helpers
// ---------------------------------------------------------------------------

// form inputs need to be named to match backend fields for this to work
// we will convert all backend field names to camelCase
// use camelCase for input names/ids
export const processValidationErrors = (
  inputs: IFormInputs,
  errors?: GqlValidationError[]
): IFormInputs => {
  if (!errors || !errors.length) {
    return inputs;
  }

  const processedInputs = mapValues(inputs, input => {
    const newInput: IFormInput = { ...input };
    const error = errors.find(
      error => camelCase(error.field) === input.id || kebabCase(error.field) === input.id
    );

    if (error) {
      newInput.error = [...newInput.error, { code: error.error, message: error.message }];
    }
    return newInput;
  });
  return processedInputs;
};

// ---------------------------------------------------------------------------
// Error code helpers
// ---------------------------------------------------------------------------

export const isErrorCodeEqualTo = (
  response: string | AppError,
  code: ErrorCodes | ErrorCodes[]
): boolean => {
  const errorCode = isString(response) ? response : response.code;

  return isString(code)
    ? isEqual(errorCode, code)
    : some(code, (codeItem: string) => isEqual(errorCode, codeItem));
};

export const assertDefined = <T>(value: T | null | undefined): T => {
  if (value == null) {
    throw new Error("Unexpected null mutation response");
  }
  return value;
};

/**
 * Returns true if the given error is a polling/retry sentinel.
 * Delegates to PollingError.codes as the single source of truth.
 */

export const isPollingError = (err: GraphQLError | Error): boolean => {
  const code = (err as GraphQLError).extensions?.code ?? err.message;
  return PollingError.codes.has(code as ErrorCodes);
};
